export const COIN_POOL_ACTIVE_STATUSES = ["waiting", "locked"];
export const COIN_POOL_FAST_POLL_STATUSES = [...COIN_POOL_ACTIVE_STATUSES, "settling"];

export const COIN_POOL_STATUS_LABEL = {
  waiting: "Open",
  locked: "Toss soon",
  settling: "Settling",
  complete: "Done",
  canceled: "Canceled",
};

export const COIN_POOL_ORDER_STATUS_LABEL = {
  open: "Open",
  partial: "Partial",
  filled: "Matched",
  cancelled: "Cancelled",
};

export const COIN_POOL_FILL_MODE_LABEL = {
  partial: "Partial",
  exact: "Exact",
};

export const COIN_POOL_FILL_MODE_DESC = {
  partial: "Any size (FIFO)",
  exact: "Exact size only",
};

export const COIN_SIDE_LABEL = {
  head: "Head",
  tail: "Tail",
};

export function formatCoinPoolCoin(value) {
  const n = Math.floor(Number(value) || 0);
  return n.toLocaleString("en-US");
}

export function getLocalDayBounds(date = new Date()) {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { since: start.toISOString(), until: end.toISOString() };
}

export function makeCoinPoolJoinIdempotencyKey() {
  return `coin-pool-order-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function coinPoolStatusTone(status) {
  switch (status) {
    case "waiting":
    case "locked":
      return "pending";
    case "complete":
      return "success";
    case "canceled":
      return "muted";
    default:
      return "default";
  }
}

export function orderStatusTone(status) {
  switch (status) {
    case "open":
      return "open";
    case "partial":
      return "partial";
    case "filled":
      return "filled";
    case "cancelled":
      return "cancelled";
    default:
      return "default";
  }
}

export function userEntriesForRound(round, userId) {
  if (!round || !userId) return [];
  const uid = String(userId);
  const fromEntries = (round.entries || []).filter((entry) => String(entry.userId) === uid);
  const fromPayload = Array.isArray(round.myEntries)
    ? round.myEntries
    : round.myEntry
      ? [round.myEntry]
      : [];

  const bySide = new Map();
  for (const entry of fromPayload) {
    if (entry?.side) bySide.set(entry.side, entry);
  }
  for (const entry of fromEntries) {
    if (entry?.side) bySide.set(entry.side, entry);
  }
  return Array.from(bySide.values());
}

export function userOrdersForRound(round, userId) {
  if (!round || !userId) return [];
  const uid = String(userId);
  if (Array.isArray(round.myOrders)) return round.myOrders;
  return (round.orders || []).filter((o) => String(o.userId) === uid);
}

/** Re-attach viewer entries/orders after socket broadcasts. */
export function attachViewerCoinPoolEntries(round, userId) {
  if (!round || !userId) return round;
  const mine = userEntriesForRound(round, userId);
  const myOrders = userOrdersForRound(round, userId);
  const next = { ...round };
  if (mine.length) {
    next.myEntries = mine;
    next.myEntry = mine[0] || null;
  } else {
    delete next.myEntries;
    delete next.myEntry;
  }
  if (myOrders.length) next.myOrders = myOrders;
  else delete next.myOrders;
  return next;
}

export function canPlaceCoinPoolOrder(round) {
  return round?.status === "waiting";
}

export function canCancelCoinPoolOrder(order) {
  return order && ["open", "partial"].includes(order.status);
}

export function poolSharePercent(stake, sideTotal) {
  const s = Math.floor(Number(stake) || 0);
  const total = Math.floor(Number(sideTotal) || 0);
  if (s <= 0 || total <= 0) return 0;
  return Math.round((s / total) * 100);
}

export function entriesForSide(entries = [], side) {
  return entries.filter((entry) => entry.side === side);
}

export function openOrdersForSide(orders = [], side) {
  return orders
    .filter(
      (o) =>
        o.side === side &&
        ["open", "partial"].includes(o.status) &&
        (o.remainingAmount ?? 0) > 0
    )
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
}

export function orderFillPercent(order) {
  const amount = Math.floor(Number(order?.amount) || 0);
  const filled = Math.floor(Number(order?.filledAmount) || 0);
  if (amount <= 0) return 0;
  return Math.min(100, Math.round((filled / amount) * 100));
}

export function formatOrderStatLine(summary) {
  const parts = [];
  if (summary.open > 0) parts.push(`${summary.open} open`);
  if (summary.partial > 0) parts.push(`${summary.partial} partial`);
  if (summary.filled > 0) parts.push(`${summary.filled} matched`);
  return parts.join(" · ");
}

export function summarizeMyOrders(orders = []) {
  return {
    open: orders.filter((o) => o.status === "open").length,
    partial: orders.filter((o) => o.status === "partial").length,
    filled: orders.filter((o) => o.status === "filled").length,
    cancelled: orders.filter((o) => o.status === "cancelled").length,
    waitingTotal: orders
      .filter((o) => ["open", "partial"].includes(o.status))
      .reduce((sum, o) => sum + (o.remainingAmount ?? 0), 0),
    matchedTotal: orders.reduce((sum, o) => sum + (o.filledAmount ?? 0), 0),
  };
}
